import type { TaskLinkRequest } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { useArtefacts } from "../conversation/artefacts/queries.ts";
import { useDebounced } from "../conversation/ConversationList.tsx";
import { useConversation, useConversationPages } from "../conversation/queries.ts";
import { useReloadHold } from "../shell/build-refresh.ts";
import { TaskError, TaskSelect, writeError } from "./parts.tsx";
import { useTaskWrite } from "./queries.ts";

export function TaskLinkPicker({
  projectId,
  taskId,
  onClose,
}: {
  projectId: string;
  taskId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState("files");
  const [conversation, setConversation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const write = useTaskWrite(projectId, taskId);
  useReloadHold(write.isPending);
  const choose = async (data: TaskLinkRequest) => {
    if (write.isPending) {
      return;
    }
    setError(null);
    try {
      await write.mutateAsync({ kind: "link", data });
      onClose();
    } catch (e) {
      setError(writeError(e, t));
    }
  };
  return (
    <AppDialog
      open
      onClose={() => {
        if (!write.isPending) {
          onClose();
        }
      }}
      title={t("tasks.addSource")}
    >
      <TaskSelect
        label={t("tasks.sources")}
        value={kind}
        disabled={write.isPending}
        onChange={(v) => {
          setKind(v);
          setConversation(null);
        }}
        options={[
          { value: "files", label: t("tasks.files") },
          { value: "conversations", label: t("tasks.conversations") },
        ]}
      />
      {kind === "files" ? (
        <FileChoices projectId={projectId} disabled={write.isPending} choose={choose} />
      ) : conversation ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            disabled={write.isPending}
            onClick={() => setConversation(null)}
          >
            {t("tasks.backSources")}
          </Button>
          <ConversationChoices
            projectId={projectId}
            conversationId={conversation}
            disabled={write.isPending}
            choose={choose}
          />
        </>
      ) : (
        <ConversationList
          projectId={projectId}
          disabled={write.isPending}
          select={setConversation}
        />
      )}
      {error ? <InlineError>{error}</InlineError> : null}
    </AppDialog>
  );
}
function FileChoices({
  projectId,
  disabled,
  choose,
}: {
  projectId: string;
  disabled: boolean;
  choose: (data: TaskLinkRequest) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const files = useQuery({
    queryKey: ["task-source-files", projectId],
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () =>
      ok(await api.api.projects[":projectId"].documents.$get({ param: { projectId } })),
  });
  if (files.isPending) {
    return <p role="status">{t("app.loading")}</p>;
  }
  if (files.isError) {
    return <TaskError error={files.error} retry={() => void files.refetch()} />;
  }
  const items = files.data.documents.filter((d) =>
    d.title.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  return (
    <>
      <Input
        type="search"
        aria-label={t("tasks.files")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {items.length ? (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {items.map((d) => (
            <li key={d.id}>
              <Button
                variant="ghost"
                disabled={disabled}
                className="h-auto w-full justify-start whitespace-normal text-left break-words"
                onClick={() => choose({ kind: "document", id: d.id })}
              >
                {d.title}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-muted">{t("tasks.nothing")}</p>
      )}
    </>
  );
}
function ConversationList({
  projectId,
  disabled,
  select,
}: {
  projectId: string;
  disabled: boolean;
  select: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const settled = useDebounced(search);
  const query = useConversationPages({ projectId, viaProject: true, q: settled });
  return (
    <>
      <p className="text-sm text-fg-muted">{t("tasks.chooseConversation")}</p>
      <Input
        type="search"
        aria-label={t("tasks.conversations")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {query.isPending ? (
        <p role="status">{t("app.loading")}</p>
      ) : query.isError ? (
        <TaskError error={query.error} retry={() => void query.refetch()} />
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {query.data.pages
            .flatMap((p) => p.items)
            .map((c) => (
              <li key={c.id}>
                <Button
                  variant="ghost"
                  disabled={disabled}
                  className="h-auto w-full justify-start whitespace-normal text-left break-words"
                  onClick={() => select(c.id)}
                >
                  {c.title ?? t("tasks.conversation")}
                </Button>
              </li>
            ))}
        </ul>
      )}
      {query.hasNextPage ? (
        <Button
          variant="outline"
          disabled={disabled || query.isFetching}
          onClick={() => void query.fetchNextPage()}
        >
          {t("tasks.more")}
        </Button>
      ) : null}
    </>
  );
}
function ConversationChoices({
  projectId,
  conversationId,
  disabled,
  choose,
}: {
  projectId: string;
  conversationId: string;
  disabled: boolean;
  choose: (data: TaskLinkRequest) => void;
}) {
  const { t } = useTranslation();
  const detail = useConversation(conversationId);
  const artefacts = useArtefacts({ projectId, conversationId });
  if (detail.isPending) {
    return <p role="status">{t("app.loading")}</p>;
  }
  if (detail.isError) {
    return <TaskError error={detail.error} retry={() => void detail.refetch()} />;
  }
  return (
    <div className="flex flex-col gap-3">
      <Button
        variant="outline"
        disabled={disabled}
        onClick={() => choose({ kind: "conversation", id: conversationId })}
      >
        {t("tasks.conversation")}: {detail.data.title ?? t("tasks.conversation")}
      </Button>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {detail.data.messages
          .filter((m) => !m.hidden)
          .map((m) => (
            <li key={m.id}>
              <Button
                variant="ghost"
                className="h-auto w-full justify-start whitespace-normal text-left break-words"
                disabled={disabled}
                onClick={() => choose({ kind: "message", id: m.id, conversationId })}
              >
                {t("tasks.message")}:{" "}
                {m.parts
                  .flatMap((p) =>
                    p &&
                    typeof p === "object" &&
                    "type" in p &&
                    p.type === "text" &&
                    "text" in p &&
                    typeof p.text === "string"
                      ? [p.text]
                      : [],
                  )
                  .join(" ")
                  .slice(0, 180)}
              </Button>
            </li>
          ))}
      </ul>
      {artefacts.isError ? (
        <TaskError error={artefacts.error} retry={() => void artefacts.refetch()} />
      ) : (
        artefacts.data?.artefacts.map((a) => (
          <Button
            key={a.id}
            variant="outline"
            className="h-auto justify-start whitespace-normal text-left break-words"
            disabled={disabled}
            onClick={() => choose({ kind: "artefact", id: a.id, conversationId })}
          >
            {t("tasks.artefact")}: {a.title}
          </Button>
        ))
      )}
    </div>
  );
}
